/**
 * persona-check-dialog.tsx — "Kiểm tra chất lượng nhân vật": AI tìm mâu thuẫn, chỉ dẫn mơ hồ,
 * yêu cầu không khả thi, quy tắc còn thiếu; cộng kiểm tĩnh (quá dài, chỗ trống chưa điền).
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { checkPersona, type PersonaIssue } from './train-api'

const TYPE_KEYS: PersonaIssue['type'][] = ['conflict', 'vague', 'infeasible', 'missing', 'too_long', 'placeholder']
const SEV: Record<PersonaIssue['severity'], { variant: 'destructive' | 'warning' | 'secondary' }> = {
  high: { variant: 'destructive' },
  medium: { variant: 'warning' },
  low: { variant: 'secondary' },
}

export function PersonaCheckDialog({ open, onOpenChange, text, onOptimize }: {
  open: boolean
  onOpenChange: (o: boolean) => void
  text: string
  /** Mở "Tối ưu bản hiện tại" kèm yêu cầu sửa các vấn đề. */
  onOptimize: (request: string) => void
}) {
  const { t } = useTranslation('aiTrainUi')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<{ summary: string; score: number | null; issues: PersonaIssue[] } | null>(null)

  useEffect(() => {
    if (!open) return
    let alive = true
    setResult(null)
    setError('')
    setBusy(true)
    checkPersona(text)
      .then((r) => { if (alive) setResult(r) })
      .catch((err) => { if (alive) setError(apiError(err)) })
      .finally(() => { if (alive) setBusy(false) })
    return () => { alive = false }
    // Chỉ chạy khi mở hộp — không chạy lại theo từng phím gõ.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const issues = result?.issues ?? []
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> {t('personaCheck.title')}</DialogTitle>
          <DialogDescription>{t('personaCheck.description')}</DialogDescription>
        </DialogHeader>
        {busy && <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('personaCheck.checking')}</div>}
        {error && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
        {result && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-lg bg-muted/40 p-3">
              {result.score != null && (
                <div className={`text-2xl font-bold ${result.score >= 80 ? 'text-success' : result.score >= 50 ? 'text-warning' : 'text-destructive'}`}>{result.score}</div>
              )}
              <p className="text-sm">{result.summary || (issues.length ? t('personaCheck.someIssues') : t('personaCheck.noneSignificant'))}</p>
            </div>
            {issues.length === 0 ? (
              <p className="flex items-center gap-2 text-sm text-success"><CheckCircle2 className="h-4 w-4" /> {t('personaCheck.noIssues')}</p>
            ) : (
              <ul className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
                {issues.map((it, i) => (
                  <li key={i} className="rounded-lg border p-3">
                    <div className="mb-1 flex flex-wrap items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                      <Badge variant={SEV[it.severity].variant}>{t(`personaCheck.severity.${it.severity}`)}</Badge>
                      <Badge variant="outline">{TYPE_KEYS.includes(it.type) ? t(`personaCheck.type.${it.type}`) : it.type}</Badge>
                    </div>
                    <p className="text-sm">{it.message}</p>
                    {it.excerpt && <p className="mt-1 rounded bg-muted/50 px-2 py-1 font-mono text-[11px] text-muted-foreground">“{it.excerpt}”</p>}
                    {it.suggestion && <p className="mt-1 text-xs text-muted-foreground">{t('personaCheck.suggestion', { text: it.suggestion })}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common:actions.close')}</Button>
          {issues.length > 0 && (
            <Button onClick={() => {
              onOpenChange(false)
              onOptimize(`Sửa các vấn đề sau:\n${issues.map((i) => `- ${i.message}${i.suggestion ? ` (gợi ý: ${i.suggestion})` : ''}`).join('\n')}`.slice(0, 1000)) // i18n-ignore: yêu cầu gửi cho AI
            }}>
              {t('personaCheck.fixByAi')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
